import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameMonth,
  isWeekend,
  startOfWeek,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { DubbleSideButton, Tag } from '@repo/zolintra-ui';
import { VIEW_DOMAIN } from '../../../constants';
import styles from './PlanningAgenda.module.css';

function weekAnchor(year, month) {
  const today = new Date();
  const viewedMonth = new Date(Number(year), Number(month), 1);
  const anchor = isSameMonth(today, viewedMonth) ? today : viewedMonth;
  return startOfWeek(anchor, { weekStartsOn: 1 });
}

function slotIsCovered(event, slotId) {
  return event.includedStartSlotId <= slotId && slotId < event.excludedEndSlotId;
}

export function PlanningAgenda({
  year,
  month,
  resources,
  postsByResource,
  getSlotsIdFromDate,
  getPostStatusTagStyling,
  isDayClickDisabled,
  onDayClick,
  onEventClick,
  canViewEventDetails,
  todayFocus,
  t,
}) {
  const [weekStart, setWeekStart] = useState(() => weekAnchor(year, month));
  const monthDate = useMemo(
    () => new Date(Number(year), Number(month), 1),
    [year, month],
  );

  useEffect(() => {
    setWeekStart(weekAnchor(year, month));
  }, [year, month]);

  useEffect(() => {
    if (todayFocus) {
      setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    }
  }, [todayFocus]);

  const minWeek = startOfWeek(monthDate, { weekStartsOn: 1 });
  const maxWeek = startOfWeek(new Date(Number(year), Number(month) + 1, 0), { weekStartsOn: 1 });

  const days = useMemo(() => (
    eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(weekStart, { weekStartsOn: 1 }),
    }).filter((day) => !isWeekend(day) && isSameMonth(day, monthDate))
  ), [weekStart, monthDate]);

  const weekLabel = days.length
    ? [
      format(days[0], 'd MMM', { locale: fr }),
      format(days.at(-1), 'd MMM yyyy', { locale: fr }),
    ].join(' – ')
    : format(weekStart, 'MMMM yyyy', { locale: fr });

  return (
    <section className={styles.agenda}>
      <div className={styles['week-nav']}>
        <DubbleSideButton
          onLeftClick={() => {
            if (weekStart.getTime() > minWeek.getTime()) {
              setWeekStart((date) => addWeeks(date, -1));
            }
          }}
          onRightClick={() => {
            if (weekStart.getTime() < maxWeek.getTime()) {
              setWeekStart((date) => addWeeks(date, 1));
            }
          }}
          label={weekLabel}
        />
      </div>

      {days.length === 0 ? (
        <p className={styles.empty}>{t('remoteWorkingAndLeave.planningSubView.weekEmpty')}</p>
      ) : null}

      <ul className={styles.people}>
        {resources.map((resource) => (
          <li key={resource.id} className={styles.person}>
            <header className={styles['person-header']}>
              <h2 className={styles['person-name']}>{resource.name}</h2>
              {resource.position ? (
                <p className={styles['person-position']}>{resource.position}</p>
              ) : null}
            </header>
            <ul className={styles.days}>
              {days.map((day) => {
                const dayShape = {
                  date: day,
                  day: day.getDate(),
                  dayOfWeek: day.getDay(),
                  month: day.getMonth(),
                  year: day.getFullYear(),
                };
                const { startSlotId, endSlotId } = getSlotsIdFromDate(day);
                const events = postsByResource?.[resource.id] || [];
                const creationDisabled = isDayClickDisabled({ day: dayShape, resource });

                return (
                  <li key={day.toISOString()} className={styles.day}>
                    <p className={styles['day-label']}>{format(day, 'EEEE d', { locale: fr })}</p>
                    <Slot
                      label={t('post.dayTime.morning')}
                      events={events.filter((event) => slotIsCovered(event, startSlotId))}
                      creationDisabled={creationDisabled}
                      onCreate={() => onDayClick([day], resource)}
                      onEventClick={onEventClick}
                      canViewEventDetails={canViewEventDetails}
                      resourceId={resource.id}
                      getPostStatusTagStyling={getPostStatusTagStyling}
                      t={t}
                    />
                    <Slot
                      label={t('post.dayTime.afternoon')}
                      events={events.filter((event) => slotIsCovered(event, endSlotId))}
                      creationDisabled={creationDisabled}
                      onCreate={() => onDayClick([day], resource)}
                      onEventClick={onEventClick}
                      canViewEventDetails={canViewEventDetails}
                      resourceId={resource.id}
                      getPostStatusTagStyling={getPostStatusTagStyling}
                      t={t}
                    />
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Slot({
  label,
  events,
  creationDisabled,
  onCreate,
  onEventClick,
  canViewEventDetails,
  resourceId,
  getPostStatusTagStyling,
  t,
}) {
  if (!events.length) {
    return (
      <button
        type="button"
        className={styles.slot}
        disabled={creationDisabled}
        onClick={onCreate}
      >
        <span>{label}</span>
        <span>{t('remoteWorkingAndLeave.newBtnLabel')}</span>
      </button>
    );
  }

  return (
    <div className={styles['slot-events']}>
      <span className={styles['slot-label']}>{label}</span>
      {events.map((event) => {
        const tag = getPostStatusTagStyling(event.status);
        const canOpen = canViewEventDetails({ type: event.type, resourceId });
        const typeLabel = event.type === VIEW_DOMAIN.LEAVE
          ? t('remoteWorkingAndLeave.leaveBtnLabel')
          : t('remoteWorkingAndLeave.remoteWorkingBtnLabel');

        return (
          <button
            key={event.id}
            type="button"
            className={styles.event}
            disabled={!canOpen}
            onClick={() => onEventClick(event)}
          >
            <Tag color={tag.color} size="small">{t(tag.key)}</Tag>
            <span>{typeLabel}</span>
          </button>
        );
      })}
    </div>
  );
}

Slot.propTypes = {
  label: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    status: PropTypes.string,
  })).isRequired,
  creationDisabled: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onEventClick: PropTypes.func.isRequired,
  canViewEventDetails: PropTypes.func.isRequired,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  getPostStatusTagStyling: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

PlanningAgenda.propTypes = {
  year: PropTypes.string.isRequired,
  month: PropTypes.string.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    position: PropTypes.string,
  })).isRequired,
  postsByResource: PropTypes.shape({}),
  getSlotsIdFromDate: PropTypes.func.isRequired,
  getPostStatusTagStyling: PropTypes.func.isRequired,
  isDayClickDisabled: PropTypes.func.isRequired,
  onDayClick: PropTypes.func.isRequired,
  onEventClick: PropTypes.func.isRequired,
  canViewEventDetails: PropTypes.func.isRequired,
  todayFocus: PropTypes.number,
  t: PropTypes.func.isRequired,
};

PlanningAgenda.defaultProps = {
  postsByResource: {},
  todayFocus: 0,
};

export default PlanningAgenda;
